import React, { useState, useEffect, useCallback } from 'react';
import { MdClose, MdAccessTime, MdFolder, MdFileDownload } from 'react-icons/md';

const VersionPreviewModal = ({ 
  isOpen, 
  onClose, 
  username,
  environment,
  filename,
  onLoadVersion,
  getClassContext
}) => {
  const [versionData, setVersionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp.slice(0, 8).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
    const time = timestamp.slice(9, 15).replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2:$3');
    return `${date.toLocaleDateString()} ${time}`;
  };

  // Extract environment from filename
  const extractEnvironmentFromFilename = (filename) => {
    const parts = filename.split('-');
    if (parts.length >= 3) {
      return parts.slice(2).join('-').replace('.json', '');
    }
    return 'unknown';
  };

  // Fetch version data for preview
  const fetchVersionData = useCallback(async () => {
    if (!filename || !username || !environment) return;

    setLoading(true);
    setError('');

    try {
      // Get class context if available
      const classContext = getClassContext ? getClassContext() : null;
      
      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        'User-Role': 'Student',
        'Requesting-User': username
      };
      
      // Add class context headers if available
      if (classContext && classContext.class_id) {
        headers['Class-ID'] = classContext.class_id.toString();
      }
      if (classContext && classContext.class_code) {
        headers['Class-Code'] = classContext.class_code;
      }
      
      const response = await fetch(`/api/saved-works/${username}/${environment}/${filename}`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const workData = await response.json();
        setVersionData(workData);
      } else {
        throw new Error(`Failed to load version: ${response.status}`);
      }
    } catch (err) {
      console.error('Error loading version data:', err);
      setError('Failed to load version data');
    } finally {
      setLoading(false);
    }
  }, [filename, username, environment, getClassContext]);

  // Load version data when modal opens
  useEffect(() => {
    if (isOpen && filename) {
      fetchVersionData();
    } else {
      setVersionData(null);
      setError('');
    }
  }, [isOpen, filename, username, environment, fetchVersionData]);

  // Handle loading the version
  const handleLoadVersion = () => {
    if (versionData && onLoadVersion) {
      onLoadVersion(versionData);
      onClose();
    }
  };

  // Handle downloading notation
  const handleDownloadNotation = () => {
    if (!versionData?.erdState?.inputText) return;
    
    try {
      const blob = new Blob([versionData.erdState.inputText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `erd-notation-${environment.toLowerCase()}-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading notation:', error);
      alert('Error downloading notation. Please try again.');
    }
  };

  // Handle downloading SVG
  const handleDownloadSVG = () => {
    if (!versionData?.erdState?.svgContent) return;
    
    let svgData = versionData.erdState.svgContent;
    
    // Check if the SVG content is base64 encoded
    if (svgData.includes('data:image/svg+xml;base64,')) {
      const base64Match = svgData.match(/data:image\/svg\+xml;base64,([^"]*)/);
      if (base64Match) {
        try {
          svgData = atob(base64Match[1]);
        } catch (e) {
          console.error('Error decoding base64 SVG:', e);
          return;
        }
      }
    }
    
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `erd-diagram-${environment.toLowerCase()}-${Date.now()}.svg`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const timestampFromFilename = filename ? filename.split('-').slice(0, 2).join('-') + '-' + filename.split('-')[2].split('.')[0] : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[90vh] flex flex-col" style={{ marginLeft: 'calc(50% - 50vw + 160px)', marginRight: 'calc(320px + 16px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <MdAccessTime className="w-6 h-6 text-gray-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Version Preview</h2>
              {filename && (
                <p className="text-sm text-gray-500">
                  {formatTimestamp(timestampFromFilename)} • {extractEnvironmentFromFilename(filename)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MdClose className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-gray-500 mt-4">Loading version data...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && versionData && (
            <div className="p-6">
              {/* Version Info */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                  <MdFolder className="w-4 h-4 mr-2" />
                  Version Details
                </h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><strong>Environment:</strong> {versionData.metadata?.environment || environment}</p>
                  <p><strong>Saved:</strong> {formatTimestamp(timestampFromFilename)}</p>
                  <p><strong>Source:</strong> {versionData.metadata?.source || 'Unknown'}</p>
                  {versionData.metadata?.eventType && (
                    <p><strong>Event:</strong> {versionData.metadata.eventType}</p>
                  )}
                </div>
              </div>

              {/* Content Preview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* ERD Notation */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-800">ERD Notation</h3>
                    {versionData.erdState?.inputText && (
                      <button
                        onClick={handleDownloadNotation}
                        className="flex items-center gap-2 text-blue-500 hover:text-blue-700 text-sm transition-colors"
                      >
                        <MdFileDownload className="w-4 h-4" />
                        Download
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-80 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-mono text-gray-700">
                      {versionData.erdState?.inputText || 'No notation available'}
                    </pre>
                  </div>
                </div>

                {/* ERD Diagram */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-800">ERD Diagram</h3>
                    {versionData.erdState?.svgContent && (
                      <button
                        onClick={handleDownloadSVG}
                        className="flex items-center gap-2 text-blue-500 hover:text-blue-700 text-sm transition-colors"
                      >
                        <MdFileDownload className="w-4 h-4" />
                        Download SVG
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-80 overflow-auto">
                    {versionData.erdState?.svgContent ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: versionData.erdState.svgContent }}
                        className="w-full flex justify-center"
                      />
                    ) : (
                      <p className="text-gray-500 text-center py-8">
                        No diagram available for this version.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Feedback Preview (if available) */}
              {versionData.erdState?.feedback && versionData.erdState.feedback.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">AI Feedback</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {versionData.erdState.feedback.slice(0, 3).map((item, index) => (
                        <div key={index} className="text-sm">
                          <p className="text-gray-600">
                            <strong>Feedback {index + 1}:</strong> {item.content || item}
                          </p>
                        </div>
                      ))}
                      {versionData.erdState.feedback.length > 3 && (
                        <p className="text-sm text-gray-500 italic">
                          ... and {versionData.erdState.feedback.length - 3} more feedback items
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleLoadVersion}
            disabled={loading || error || !versionData}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded-md transition-colors duration-200 flex items-center gap-2"
          >
            <MdFileDownload className="w-4 h-4" />
            Load Version
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionPreviewModal;